import { Component, OnDestroy, OnInit } from "@angular/core"
import { ActivatedRoute, Params } from "@angular/router"
import { Title } from "@angular/platform-browser"
import { Attribute } from "../../models/attribute.model"
import { PremiumHomePage } from "../../models/collection/premiumHomePage.model"
import { PremiumCollectionGTM } from "../../models/gtm/collection"
import { CollectionService } from "../../services/collection.service"
import { UserService } from "../../services/user.service"
import { EduComponent } from "../../utils/eduComponent"
import Utils from "../../utils/utils"
import { GTMService } from "../../services/gtm.service"
import { combineLatest, Observable, Subject } from "rxjs"
import { map, takeUntil, tap } from "rxjs/operators"
import { StudentGateService } from "../../services/student-gate.service"
import { CollectionNode } from "../../models/collectionNode.model"
import { routeAnimations, routeTransition } from "../../utils/animations"

@Component({
    selector: "app-collection-premium",
    templateUrl: "./collection-premium.component.html",
    styleUrls: ["./collection-premium.component.scss"],
    host: { "[@routeTransition]": "true", "[@routeAnimations]": "false", style: "display: block;" },
    animations: [routeTransition, routeAnimations],
    standalone: false,
})
export class CollectionPremiumComponent extends EduComponent implements OnInit, OnDestroy {
    public collection: PremiumHomePage = null
    public isPreviewMode: boolean = true
    public footerAttributions: Attribute[]
    public collectionSlug: string = null
    private destroyed$: Subject<void> = new Subject<void>()

    isLoading$: Observable<boolean> = combineLatest([
        this.collectionService.activePremiumCollectionChange,
        this.route.params,
    ]).pipe(
        takeUntil(this.destroyed$),
        map(([collection, params]: [CollectionNode, Params]) => collection?.slug === params.slug)
    )
    studentToggleObservable: Observable<string> =
        this.studentGateService.collectionFilterChoice.pipe(
            takeUntil(this.destroyed$),
            tap((selectedOption: string) => {
                if (selectedOption && this.collectionSlug)
                    this.collectionService.getPremiumHomeData(this.collectionSlug)
            })
        )

    routeParamsObservable: Observable<any> = this.route.params.pipe(
        takeUntil(this.destroyed$),
        tap((params: Params) => {
            this.collectionSlug = params.slug
            this.collectionService.getPremiumHomeData(params.slug)
        })
    )

    collectionChange: Observable<void> = this.collectionService.activePremiumCollectionChange.pipe(
        takeUntil(this.destroyed$),
        map((response: CollectionNode) => this.setPageAttributes(response))
    )

    constructor(
        public collectionService: CollectionService,
        public userService: UserService,
        public gtmService: GTMService,
        private route: ActivatedRoute,
        private titleService: Title,
        private studentGateService: StudentGateService
    ) {
        super()
    }

    ngOnInit() {
        this.studentToggleObservable.subscribe()
        this.routeParamsObservable.subscribe()
        this.collectionChange.subscribe()
        this.isLoading$.subscribe()
    }

    buildCssSelector(text: string): string {
        return Utils.buildCssSelector(text)
    }

    setPageAttributes(apiResponse: CollectionNode) {
        if (!apiResponse.id) return
        this.collection = PremiumHomePage.fromData(apiResponse)
        this.footerAttributions = this.collection.getFooterAttributions()
        this.titleService.setTitle(this.collection.metaTitle)
        window.scroll(0, 0)
        this.updateGTM()
        this.isPreviewMode = window.location.pathname.includes("/preview/")
        this.collectionService.incrementViewCount(this.collectionSlug)
        this.emitAdminHeaderData()
    }

    updateGTM() {
        this.gtmService.resetDataLayer()
        this.gtmService.setDataLayer(new PremiumCollectionGTM(this.collection))
        this.gtmService.pushPageView()
    }

    private emitAdminHeaderData(): void {
        if (!this.collection) return

        this.userService.emitAdminHeaderData({
            title: "Collection",
            adminLink: this.collection.getAdminLink(),
            isPublished: this.collection.publish,
        })
    }

    ngOnDestroy() {
        super.ngOnDestroy()
        this.destroyed$.next()
        this.destroyed$.complete()
        this.userService.emitAdminHeaderData({})
    }
}
